import { PrismicNextImage } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { Button, CheckoutModal, ContactForm, Modal } from "@/components";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Styles from "./ProductInfo.module.css";
import { is } from "date-fns/locale";

/**
 * @typedef {import("@prismicio/client").Content.ProductInfoSlice} ProductInfoSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProductInfoSlice>} ProductInfoProps
 * @param {ProductInfoProps}
 */
const ProductInfo = ({ courseId, slice: { primary, items } }) => {
	const todaysDate = new Date();
	const router = useRouter();
	const [adminCourses, setAdminCourses] = useState([]);
	const [checkoutModal, setCheckoutModal] = useState({
		show: false,
		course: null,
		status: false,
	});
	const [modalOpen, setModalOpen] = useState(false);

	const productImages = items?.map((item, index) => (
		<div className={Styles.imageContainer} key={index}>
			<PrismicNextImage field={item?.image} className={Styles.productImage} />
		</div>
	));

	console.debug("Course ID: ");
	console.debug(courseId);

	let validCourses = [];

	if (courseId !== null && courseId !== undefined) {
		//Fetch admin courses for this courseId
		useEffect(() => {
			fetch(`/api/admin-db/get-template-courses?courseId=${courseId}`)
				.then((response) => response.json())
				.then((data) => setAdminCourses(data))
				.catch((error) => console.error("Error:", error));
		}, []);

		console.debug("Courses from DB: ", adminCourses);

		// Validate courses and filter to new array that only contains published courses that have dates after todays date
		validCourses = adminCourses?.filter((course) => {
			const [year, month, day] = course.startDate.split("-");
			const startDateUTC = new Date(Date.UTC(year, month - 1, day));
			return startDateUTC > todaysDate;
			// return course.published && startDateUTC > todaysDate;
		});

		validCourses.sort((a, b) => {
			const [yearA, monthA, dayA] = a.startDate.split("-");
			const dateA = Date.UTC(yearA, monthA - 1, dayA);

			const [yearB, monthB, dayB] = b.startDate.split("-");
			const dateB = Date.UTC(yearB, monthB - 1, dayB);

			return dateA - dateB;
		});

		console.debug(
			"Valid Courses: ",
			validCourses,
			"\nLength: ",
			validCourses.length
		);
	}

	// Check if attendees or instructors state allow for course to be bookable
	const isCourseFull = (course) => {
		// Check if initial max attendees is reached - always allow booking even if first set of instructors not assigned
		const isAttendeesNotFull = course.attendees.length < course.attendeeRatio;

		// How many instructor sets are required
		const attendeeRequiredInstructorSets = Math.ceil(
			course.attendees.length / course.attendeeRatio
		);

		// Calculate the total number of instructors from all arrays
		const totalInstructors = course.instructorInput.reduce(
			(total, dayInstructors) => {
				return total + dayInstructors.id.length;
			},
			0
		);

		// How many instructor sets have been assigned
		const assignedInstructorSets = Math.ceil(
			totalInstructors / course.duration
		);

		// Allow for additional booking only if instructors assigned beyond the initial set
		const isExtraInstructorAssigned =
			assignedInstructorSets > attendeeRequiredInstructorSets;

		return isAttendeesNotFull || isExtraInstructorAssigned;
	};

	useEffect(() => {
		// Check to see if this is a redirect back from Checkout
		const query = new URLSearchParams(window.location.search);
		if (query.get("success")) {
			console.log("Order placed! You will receive an email confirmation.");
			setCheckoutModal({ show: true, status: "success" });
		}

		if (query.get("canceled")) {
			console.log(
				"Order canceled -- continue to shop around and checkout when you’re ready."
			);
			setCheckoutModal({ show: true, status: "canceled" });
		}
	}, []);

	const handleModalClose = () => {
		// Get a copy of the current query object
		const newQuery = { ...router.query };

		// Check if the parameters exist
		const hasSuccess = "success" in newQuery;
		const hasCanceled = "canceled" in newQuery;

		// Remove the parameters
		delete newQuery.success;
		delete newQuery.canceled;

		// Only call router.replace if the parameters existed
		if (hasSuccess || hasCanceled) {
			router.replace(
				{
					pathname: router.pathname,
					query: newQuery,
				},
				undefined,
				{ scroll: false }
			);
		}

		// Close the modal
		setCheckoutModal({ show: false });
	};

	return (
		<section className={Styles.wrapper}>
			<div className={Styles.container} data-contain="true">
				<div className={Styles.col}>
					<Carousel
						emulateTouch={true}
						infiniteLoop={true}
						animationHandler="fade"
						renderThumbs={() => productImages}
						showStatus={false}
						thumbWidth={120}
						showIndicators={false}
					>
						{productImages && productImages}
					</Carousel>
				</div>
				<div className={Styles.col}>
					<span className={Styles.heading}>
						<PrismicRichText field={primary?.heading} />
					</span>
					{courseId ? (
						<>
							{validCourses?.length > 0 ? (
								<>
									<div className={Styles.bookedCourses}>
										{/* <ArrowDownwardIcon className={Styles.scrollIconDown} /> */}

										{validCourses?.map((course, index) => {
											// Check if course is bookable. If yes book button or ratio maxed inquire button. Else inquire about course inquire button
											if (course?.bookable) {
												isCourseFull(course);
												return (
													<div className={Styles.bookedCoursesCard} key={index}>
														<div className={Styles.dateContainer}>
															<span>
																Start Date:{" "}
																{(() => {
																	let parts = course.startDate.split("-");
																	let localDate = new Date(
																		parts[0],
																		parts[1] - 1,
																		parts[2]
																	);
																	return localDate.toLocaleDateString();
																})()}
															</span>
															<span>
																End Date:{" "}
																{(() => {
																	let parts = course.endDate.split("-");
																	let localDate = new Date(
																		parts[0],
																		parts[1] - 1,
																		parts[2]
																	);
																	return localDate.toLocaleDateString();
																})()}{" "}
															</span>
														</div>
														{/* Check if either there are not a full class of attendees or if additional instructors have been assigned to the course */}
														{isCourseFull(course) ? (
															<Button
																label="Book Today"
																onClick={() => {
																	setCheckoutModal({
																		show: true,
																		course: course,
																	});
																}}
																type={"button"}
															/>
														) : (
															<>
																<span className={Styles.bookedText}>
																	This course is currently full but we may be
																	able to make room!
																</span>
																<Button
																	label="Inquire"
																	onClick={() => setModalOpen(true)}
																	type={"button"}
																	style={{ flexShrinK: "0" }}
																/>
															</>
														)}
													</div>
												);
											} else {
												/* If courses are published but not bookable, render CTA buttons */
												return (
													<div className={Styles.bookedCoursesCard} key={index}>
														<div className={Styles.dateContainer}>
															<span>
																Start Date:{" "}
																{(() => {
																	let parts = course.startDate.split("-");
																	let localDate = new Date(
																		parts[0],
																		parts[1] - 1,
																		parts[2]
																	);
																	return localDate.toLocaleDateString();
																})()}
															</span>
															<span>
																End Date:{" "}
																{(() => {
																	let parts = course.endDate.split("-");
																	let localDate = new Date(
																		parts[0],
																		parts[1] - 1,
																		parts[2]
																	);
																	return localDate.toLocaleDateString();
																})()}{" "}
															</span>
														</div>
														<>
															<span className={Styles.bookedText}>
																Reach out to check availability of this course!
															</span>
															{/* TODO:: This button needs to hook into form */}
															<Button
																id="cta2"
																label="Inquire"
																onClick={() => setModalOpen(true)}
																type={"button"}
																style={{ flexShrinK: "0" }}
															/>
														</>
													</div>
												);
											}
										})}
									</div>
									{/* This CTA renders when there are valid courses available to display */}
									<Button
										id="training-hub-cta"
										onClick={() => router.push("/training/hub")}
										type={"button"}
										label="See Full Training Calendar"
									/>
								</>
							) : (
								// This CTA renders when there are no valid courses available to display
								<Button
									id="contact-us-modal-cta3"
									onClick={() => setModalOpen(true)}
									type={"button"}
									label={primary?.cta_label}
								/>
							)}
						</>
					) : (
						// This CTA renders when there are no valid courses available to display

						<Button
							id="contact-us-modal-cta2"
							onClick={() => setModalOpen(true)}
							type={"button"}
							label={primary?.cta_label}
						/>
					)}

					<span className={Styles.description}>
						<PrismicRichText field={primary?.product_description} />
					</span>
				</div>
			</div>

			{/* Checkout nd contact modals */}
			<Modal
				modalOpen={checkoutModal.show}
				setModalOpen={handleModalClose}
				title="Book Your Training Course"
			>
				<CheckoutModal
					course={checkoutModal.course}
					status={checkoutModal.status}
					setModalOpen={handleModalClose}
				/>
			</Modal>
			<Modal
				title="Contact Us"
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
			>
				<ContactForm setModalOpen={setModalOpen} />
			</Modal>
		</section>
	);
};

export default ProductInfo;
